import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { exchangePreviewToken } from "../api";
import {
  clearPreviewAccessToken,
  hasExplicitPreviewMarker,
  hasPreviewSession,
  setPreviewAccessToken,
  setPreviewProfile,
  setPreviewQuestionnaireSlug,
} from "../utils/previewSession";
import { audienceFromGrupoSlug } from "../../utils/anamneseVersion";
import Spinner from "../../components/Spinner";

import "../../assets/styles/chat.css";
import "../../assets/styles/anamnese-v2.css";

/**
 * Preview: o token da URL é trocado na API. Não exige login no site.
 */
const PreviewAdminRoute = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const previewToken = searchParams.get("preview_token");
  const wantsPreview = hasExplicitPreviewMarker({
    search: location.search,
    state: location.state,
  });

  const [status, setStatus] = useState(() => {
    if (previewToken) {
      return "exchanging";
    }
    if (wantsPreview && hasPreviewSession()) {
      return "ready";
    }
    if (wantsPreview) {
      return "invalid";
    }
    return "pass";
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!previewToken) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await exchangePreviewToken(previewToken);
        if (cancelled) {
          return;
        }
        if (!data?.token) {
          throw new Error("A API não devolveu a sessão de preview.");
        }
        setPreviewAccessToken(data.token);
        setPreviewProfile(data.profile ?? null);
        const questionnaireSlug = data?.questionnaire?.slug ?? null;
        setPreviewQuestionnaireSlug(questionnaireSlug);
        const next = new URLSearchParams(searchParams);
        next.delete("preview_token");
        next.set("is_preview", "1");
        // Kids/baby: ProfileStep e rotas comerciais precisam do audience na URL.
        const audience = audienceFromGrupoSlug(questionnaireSlug);
        if (audience && audience !== "adulto") {
          next.set("audience", audience);
        } else {
          next.delete("audience");
        }
        const personaId = data?.persona?.id;
        if (
          personaId &&
          !next.get("persona") &&
          !next.get("persona_id")
        ) {
          next.set("persona_id", String(personaId));
          next.delete("fresh");
        }
        setSearchParams(next, { replace: true });
        setStatus("ready");
      } catch (err) {
        if (cancelled) {
          return;
        }
        clearPreviewAccessToken();
        setError(
          err?.response?.data?.message ||
            "O link de preview é inválido ou expirou.",
        );
        setStatus("invalid");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [previewToken, searchParams, setSearchParams]);

  useEffect(() => {
    if (status === "pass" && hasPreviewSession()) {
      clearPreviewAccessToken();
    }
  }, [status]);

  if (status === "pass") {
    return children;
  }

  if (status === "exchanging") {
    return (
      <div className="container" style={{ maxWidth: "1120px" }}>
        <Spinner minHeight="60vh" title="Carregando..." />
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="Chat anamnese-v2">
        <div className="anamnese-v2-page-error">
          <div className="anamnese-v2-alert anamnese-v2-alert-danger" role="alert">
            {error ||
              "O preview da anamnese é restrito a administradores. Abra o link pelo painel."}
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default PreviewAdminRoute;
